import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { useParams /*Route, Switch, NavLink*/ } from 'react-router-dom';
import { Row, /*Nav,*/ Col, Tabs, Tab } from 'react-bootstrap';
import { getStatementTypesByProject, getProjectStatementsByState, getProjectTotalsReportedAmount } from '@lib/api';
import { dateFormatter, formatToDecimals } from '@lib/helpers/formatting';
import { columnCentered } from '@lib/helpers/agGridHelpers';
import OETableAgGrid from '@components/oetable/OETableAgGrid';
import MakeReporting from '@components/projectStatements/MakeReporting';
import MakeReportingModal from 'components/projectStatements/MakeReportingModal';
import DataHeader from 'components/DataHeader';
import { IcoCheck } from 'icone/index';
import Dnsh from './Dnsh';
import PeriodicReporting from './PeriodicReporting';
import ContinuousReporting from './ContinuousReporting';
import { UserContext } from 'components/contexts/UserProvider';
// import { WithPermission } from '@lib/helpers/WithPermission';
import NavTabs from '@components/atomic/molecules/NavTabs/NavTabs';
import { useHasPermission } from '@lib/hooks/useHasPermission';
import SendDataRegis from '@components/projectStatements/SendDataRegis';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const Reporting = ({ project }) => {
	const queryClient = useQueryClient();

	const { projectId /*, tab, submenu*/ } = useParams();
	const { isOe, userAccess } = useContext(UserContext);
	// const [statements, setStatements] = useState(null);
	const [validatedStatements, setValidatedStatements] = useState([]);
	const [validatedStatementsMeta, setValidatedStatementsMeta] = useState({});
	const [draftStatements, setDraftStatements] = useState(null);
	const [draftStatementsMeta, setDraftStatementsMeta] = useState({});
	const [totals, setTotals] = useState({});
	const [optionsStatementTypes, setOptionsStatementTypes] = useState([]);
	// const [activeKey, setActiveKey] = useState(submenu ?? 'history');
	const [refresh, setRefresh] = useState(false);
	const [isInCreate, setIsInCreate] = useState(false);
	const localLang = `${localStorage.getItem('userLanguage').toLowerCase()}-${localStorage.getItem('userLanguage')}`;
	const isProjectWp = project.streamConfigurationType === 'WP';
	const isRegisProject = useMemo(
		() => !isProjectWp && project?.notice?.institution?.managingSystem?.toLowerCase() === 'regis',
		[project, isProjectWp],
	);
	const isEnabledRegisConfig = useMemo(
		() => project?.advancedConfig?.some((el) => el.name === 'regis_interoperability' && el.enabled),
		[project],
	);

	const canViewHistoryApprovalTab = useHasPermission(['view-sal-history-approval'], 'all');

	const canViewHistoryExternalCostsTab = useHasPermission(['view-sal-history-external-costs'], 'all');
	const canViewDraftExternalCostsTab = useHasPermission(['view-sal-external-costs'], 'all');

	const canViewHistoryInternalCostsTab = useHasPermission(['view-sal-history-internal-costs'], 'all');
	const canViewDraftInternalCostsTab = useHasPermission(['view-sal-internal-costs'], 'all');

	const canViewHistoryEntityToValidate = useHasPermission(['view-sal-history-entities-to-validate'], 'all');
	const canViewDraftEntityToValidate = useHasPermission(['view-sal-entities-to-validate'], 'all');

	const canViewHistoryIntegration = useHasPermission(['view-sal-history-integration'], 'all');
	//console.log('project', project);

	const {
		myUser: { companies },
		isCoordinator,
		isPartnerUser,
	} = useContext(UserContext);
	const hasPartnerAccess = useMemo(() => (isProjectWp ? isPartnerUser : isCoordinator), [isPartnerUser, isCoordinator, isProjectWp]);

	const isGuest = !userAccess.isAboveGuest && !userAccess.isGuestEnteErogante;

	const { data: validStatementsFromApi } = useQuery({
		queryKey: ['validatedStatements', projectId],
		queryFn: () => getProjectStatementsByState(projectId, 'VALIDATED', 'validatedStatements'),
	});
	useEffect(() => {
		if (validStatementsFromApi) {
			setValidatedStatements(validStatementsFromApi.data);
			const { data, success, ...restResp } = validStatementsFromApi;
			setValidatedStatementsMeta(restResp);
		}
	}, [validStatementsFromApi]);

	const { data: draftStatementsFromApi } = useQuery({
		queryKey: ['draftStatements', projectId],
		queryFn: () => getProjectStatementsByState(projectId, 'DRAFT', 'draftStatements'),
	});

	useEffect(() => {
		if (draftStatementsFromApi) {
			setDraftStatements(draftStatementsFromApi.data);
			const { data, success, ...restResp } = draftStatementsFromApi;
			setDraftStatementsMeta(restResp);
		}
	}, [draftStatementsFromApi]);

	const { data: statementTypesFromApi } = useQuery({
		queryKey: ['statementTypes', projectId],
		queryFn: () => getStatementTypesByProject(projectId),
	});
	useEffect(() => {
		if (statementTypesFromApi) {
			setOptionsStatementTypes(
				statementTypesFromApi.data.map((types) => ({
					value: types.code,
					label: types.name,
				})),
			);
		}
	}, [statementTypesFromApi]);

	const statements = useMemo(() => validatedStatements.concat(draftStatements), [validatedStatements, draftStatements]);

	// const handleSetAllStatements = useCallback(() => {
	// 	getProjectStatementsByState(projectId, 'VALIDATED', 'validatedStatements').then((retrievedStatements) => {
	// 		if (retrievedStatements.success) {
	// 			setValidatedStatements(retrievedStatements.data);
	// 			const { data, success, ...restResp } = retrievedStatements;
	// 			setValidatedStatementsMeta(restResp);
	// 		}
	// 	});

	// 	getProjectStatementsByState(projectId, 'DRAFT', 'draftStatements').then((retrievedStatements) => {
	// 		if (retrievedStatements.success) {
	// 			setDraftStatements(retrievedStatements.data);
	// 			const { data, success, ...restResp } = retrievedStatements;
	// 			setDraftStatementsMeta(restResp);
	// 		}
	// 	});
	// }, [projectId]);

	// useEffect(() => {
	// 	setStatements(validatedStatements.concat(draftStatements));
	// }, [draftStatements, validatedStatements]);

	// useEffect(() => {
	// 	if (projectId) {
	// 		handleSetAllStatements();

	// 		getStatementTypesByProject(projectId).then((retrievedStatementTypes) => {
	// 			setOptionsStatementTypes(
	// 				retrievedStatementTypes.data.map((types) => ({
	// 					value: types.code,
	// 					label: types.name,
	// 				})),
	// 			);
	// 		});
	// 	}
	// }, [projectId, handleSetAllStatements]);

	const refreshTotals = useCallback(() => {
		getProjectTotalsReportedAmount(projectId, '?state=VALIDATED').then((retrievedTotalsReportedAmount) => {
			if (retrievedTotalsReportedAmount.success) {
				//la proprietà total è l'importo rendicontato e totalRequestedAmount è il contributo richiesto
				//in statements ci sono gli importi rendicontati
				setTotals(retrievedTotalsReportedAmount.data);
			}
		});
	}, [projectId]);

	useEffect(() => {
		refreshTotals();
	}, [refreshTotals]);

	useEffect(() => {
		if (refresh) {
			queryClient.invalidateQueries({ queryKey: ['validatedStatements', projectId] });
			queryClient.invalidateQueries({ queryKey: ['draftStatements', projectId] });
		}
	}, [refresh, projectId, queryClient]);

	// useEffect(() => {
	// 	if (refresh) {
	// 		handleSetAllStatements();

	// 		setRefresh(false);
	// 	}
	// }, [handleSetAllStatements, refresh]);

	const CheckBoxCellRenderer = useCallback((params) => {
		//console.log('params', params.data.cashedIn);

		return (
			<>
				{params.data.cashedIn ? (
					<>
						<IcoCheck as={Col} style={{ fill: '#689689', marginRight: '5px' }} />
						<span as={Col} className="small">
							{dateFormatter(params.data.cashedInDate)}
						</span>
					</>
				) : null}
			</>
		);
	}, []);

	const statementsColumn = [
		{
			headerName: 'id',
			field: 'id',
			hide: true,
		},
		{
			headerName: 'Tipo',
			field: 'statementTypeCode',
			valueGetter: (params) => {
				const type = optionsStatementTypes
					.filter((optType) => optType.value === params.data.statementTypeCode)
					.map((type) => type.label)
					.pop();
				return params.data.statementTypeCode === 'SAL' ? params.data.name : type;
			},
			autoHeight: true,
			cellStyle: {
				height: '100%',
				display: 'flex',
				alignItems: 'center',
			},
		},
		{
			headerName: 'Importo rendicontato €',
			field: 'reportedAmount',
			type: 'rightAligned',
			filter: 'agTextColumnFilter',
			minWidth: 220,
			valueGetter: (params) => {
				const requestedAmount = totals.statements
					?.filter((statement) => statement.statementId === params.data.id)
					.map((el) => el.total)
					.pop();

				return formatToDecimals(requestedAmount);
			},
			autoHeight: true,
			cellStyle: {
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'end',
			},
		},
		{
			headerName: isProjectWp ? 'Contributo massimo ottenibile €' : 'Contributo richiesto €',
			field: 'requestedAmount',
			type: 'rightAligned',
			filter: 'agTextColumnFilter',
			valueGetter: (params) => {
				return formatToDecimals(params.data.requestedAmount);
			},
			autoHeight: true,
			cellStyle: {
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'end',
			},
		},
		{
			headerName: 'Data sottomissione',
			field: 'date',
			...columnCentered(),
			valueGetter: (params) => {
				return dateFormatter(params.data.date);
			},
			autoHeight: true,
			cellStyle: {
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			},
		},
		{
			headerName: 'Contributo approvato €',
			field: 'approvedAmount',
			filter: 'agTextColumnFilter',
			type: 'rightAligned',
			resizable: true,
			valueGetter: (params) => {
				return params.data.approvedAmount ? formatToDecimals(params.data.approvedAmount) : 0;
			},
			autoHeight: true,
			minWidth: 220,
			cellStyle: {
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'end',
			},
		},
		{
			headerName: 'Incassato',
			field: 'cashedIn',
			cellRenderer: CheckBoxCellRenderer,
			cellRendererParams: {
				field: 'cashedIn',
			},
			resizable: false,
			autoHeight: true,
			...columnCentered('', {
				height: '100%',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
			}),
		},
		...(!isProjectWp
			? [
					{
						headerName: 'Contributo incassato €',
						field: 'cashedInAmount',
						resizable: true,
						valueGetter: (params) => {
							return params.data.cashedInAmount ? formatToDecimals(params.data.cashedInAmount) : 0;
						},
						autoHeight: true,
						...columnCentered('', {
							height: '100%',
							display: 'flex',
							justifyContent: 'end',
							alignItems: 'center',
						}),
					},
				]
			: []),
		// if is userRole <= 4 or role = 71 add the column for the action
		...(userAccess.isOeExternal || userAccess.isGuestEnteErogante
			? [
					{
						headerName: '',
						headerComponent: (params) => {
							return validatedStatements.length > 0 && isEnabledRegisConfig ? (
								<div className="ag-cell-label-container" style={{ justifyContent: 'center', marginRight: '35px', minWidth: '25px' }}>
									<SendDataRegis
										projectId={projectId}
										isRegisProject={isRegisProject}
										dataId={params.data?.id}
										statementTypeCode={params.data?.statementTypeCode}
										statementState={params.data?.state}
									/>
								</div>
							) : null;
						},
						minWidth: 60,
						maxWidth: 150,
						autoHeight: true,
						cellStyle: {
							height: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							textAlign: 'center',
							paddingBottom: '0.5rem',
						},
						cellRenderer: MakeReportingModal,
						cellRendererParams: {
							project: project,
							isEnabledToView: isOe || userAccess.isOeExternal || userAccess.isGuestEnteErogante,
							isWp: isProjectWp,
							isGuest,
							projectId: projectId,
							optionsStatementTypes: optionsStatementTypes,
							isEdit: false,
							isRegisProject: isRegisProject,
							setRefresh: setRefresh,
							setIsShowNew: setIsInCreate,
							refreshTotals: refreshTotals,
							canViewHistoryApprovalTab: canViewHistoryApprovalTab,
							canViewExternalCostsTab: canViewHistoryExternalCostsTab,
							canViewInternalCostsTab: canViewHistoryInternalCostsTab,
							canViewEntityToValidate: canViewHistoryEntityToValidate,
							canViewHistoryIntegration: canViewHistoryIntegration,
							isEnabledRegisConfig: isEnabledRegisConfig,
							hasEnabledAi: project?.aiEnabled,
						},
					},
				]
			: []),
	];

	const tabNavItems = [
		{
			eventKey: 'history',
			title: 'Storico rendiconti',
			transCode: 'project.reporting.history',
			content:
				project.streamConfigurationType === 'STREAM' ? (
					<>
						<DataHeader
							data={[
								{
									name: 'Importo rendicontato',
									value: formatToDecimals(totals.total) + ' €',
								},
								{
									name: isProjectWp ? 'Contributo massimo ottenibile €' : 'Contributo richiesto',
									value: formatToDecimals(totals.totalRequestedAmount) + ' €',
								},
								//DISABILITATO PER PRESENTAZIONE
								/* {
                        name: 'Importo tagli',
                        value: formatToDecimals(validatedStatements.filter(el => el.cashedIn).reduce((acc, curr) => acc + curr.approvedAmount, 0)) + ' €',
                      }, */
							]}
						/>

						{validatedStatements && (
							<OETableAgGrid
								column={statementsColumn}
								data={validatedStatements}
								dataMeta={validatedStatementsMeta}
								hideSearchBar={true}
								refTable="validatedStatements"
							/>
						)}
					</>
				) : project.streamConfigurationType === 'WP' ? (
					<Row>
						<Tabs
							style={{
								marginTop: '50px',
								marginBottom: '20px',
								border: 'none',
							}}
							// variant='pills'
							// activeKey={activeKey}
							// onSelect={(k) => setActiveKey(k)}
							// fill
						>
							<Tab eventKey="periodic" title="Periodic Reporting">
								<PeriodicReporting
									state="VALIDATED"
									userCompanies={companies}
									isProjectWp={isProjectWp}
									isEnabledToView={isOe || userAccess.isExternalAdmin || hasPartnerAccess}
								/>
							</Tab>

							<Tab eventKey="continuous" title="Continuous Reporting">
								<ContinuousReporting
									state="VALIDATED"
									isProjectWp={isProjectWp}
									isEnabledToView={isOe || userAccess.isExternalAdmin || hasPartnerAccess}
								/>
							</Tab>
						</Tabs>
					</Row>
				) : null,
			shouldBeVisible: useHasPermission('view-reports-history'),
		},
		{
			eventKey: 'expense-report',
			title: 'Gestione bozze',
			transCode: 'project.reporting.manage_drafts',
			content:
				project.streamConfigurationType === 'STREAM' ? (
					<MakeReporting
						optionsStatementTypes={optionsStatementTypes}
						draftStatements={draftStatements}
						draftStatementsMeta={draftStatementsMeta}
						statements={statements}
						localLang={localLang}
						// handleSetAllStatements={handleSetAllStatements}
						isInCreate={isInCreate}
						setIsInCreate={setIsInCreate}
						isAdvancedSettings={project?.advancedConfig?.some((el) => el.name === 'advance_payment_percentage' && el.enabled)}
						canViewHistoryApprovalTab={canViewHistoryApprovalTab}
						canViewExternalCostsTab={canViewDraftExternalCostsTab}
						canViewInternalCostsTab={canViewDraftInternalCostsTab}
						canViewEntityToValidate={canViewDraftEntityToValidate}
						canViewHistoryIntegration={canViewHistoryIntegration}
						hasEnabledAi={project?.aiEnabled}
					/>
				) : project.streamConfigurationType === 'WP' ? (
					<Row>
						<Tabs
							style={{
								marginTop: '50px',
								marginBottom: '20px',
								border: 'none',
							}}
							// variant='pills'
							// activeKey={activeKey}
							// onSelect={(k) => setActiveKey(k)}
							// fill
						>
							<Tab eventKey="periodic" title="Periodic Reporting">
								<PeriodicReporting
									state="DRAFT"
									isEnabledToView={isOe || userAccess.isExternalAdmin}
									userCompanies={companies}
									isProjectWp={isProjectWp}
									projectIndirectCostsPerc={project.indirectCostsPerc}
								/>
							</Tab>

							<Tab eventKey="continuous" title="Continuous Reporting">
								<ContinuousReporting state="DRAFT" isEnabledToView={isOe || userAccess.isExternalAdmin} isProjectWp={isProjectWp} />
							</Tab>
						</Tabs>
					</Row>
				) : null,

			shouldBeVisible: useHasPermission('view-draft-statements') && (project.streamConfigurationType === 'STREAM' || hasPartnerAccess),
		},
		{
			eventKey: 'dnsh',
			title: 'DNSH',
			transCode: 'project.reporting.dnsh',
			content: <Dnsh project={project} />,
			shouldBeVisible:
				useHasPermission('view-rendicontazione-dnsh-tab') &&
				project.advancedConfig?.find((el) => el.name === 'enable_dnsh_eligibility')?.enabled,
		},
	];

	return (
		<>
			<NavTabs
				tabNavItems={tabNavItems}
				onValueChangeCallback={() => {
					refreshTotals();
				}}
			/>
			{/* 
			<div className="candidacy simple-box scenaritab">
				<Row>
					<Nav
						variant="pills"
						activeKey={activeKey}
						onSelect={(k) => {
							setActiveKey(k);
							refreshTotals();
						}}
					>
						<WithPermission permission="view-reports-history">
							<Nav.Item>
								<Nav.Link eventKey="history" as={NavLink} to={`/project/${projectId}/${tab}/history`}>
									Storico rendiconti
								</Nav.Link>
							</Nav.Item>
						</WithPermission>
						<WithPermission permission="view-draft-statements">
							{(project.streamConfigurationType === 'STREAM' || isCoordinator || isBeneficiary) && (
								<Nav.Item>
									<Nav.Link eventKey="expense-report" as={NavLink} to={`/project/${projectId}/${tab}/expense-report`}>
										Gestione bozze
									</Nav.Link>
								</Nav.Item>
							)}
						</WithPermission>
						<WithPermission permission="view-rendicontazione-dnsh-tab">
							{project.advancedConfig?.find((el) => el.name === 'enable_dnsh_eligibility')?.enabled && (
								<Nav.Item>
									<Nav.Link eventKey="dnsh" as={NavLink} to={`/project/${projectId}/${tab}/dnsh`}>
										DNSH
									</Nav.Link>
								</Nav.Item>
							)}
						</WithPermission>
					</Nav>
				</Row>

				<Switch>
					<Route path={`/project/:projectId/:tab/history`}>
						<WithPermission permission="view-reports-history">
							{activeKey === 'history' ? (
								<>
									{project.streamConfigurationType === 'STREAM' ? (
										<>
											<DataHeader
												data={[
													{
														name: 'Importo rendicontato',
														value: formatToDecimals(totals.total) + ' €',
													},
													{
														name: isProjectWp ? 'Contributo massimo ottenibile €' : 'Contributo richiesto',
														value: formatToDecimals(totals.totalRequestedAmount) + ' €',
													},
													//DISABILITATO PER PRESENTAZIONE
					// 								{
                    //     name: 'Importo tagli',
                    //     value: formatToDecimals(validatedStatements.filter(el => el.cashedIn).reduce((acc, curr) => acc + curr.approvedAmount, 0)) + ' €',
                    //   }
												]}
											/>

											{validatedStatements && (
												<OETableAgGrid
													column={statementsColumn}
													data={validatedStatements}
													dataMeta={validatedStatementsMeta}
													hideSearchBar={true}
													refTable="validatedStatements"
												/>
											)}
										</>
									) : null}
									{project.streamConfigurationType === 'WP' ? (
										<Row>
											<Tabs
												style={{
													marginTop: '50px',
													marginBottom: '20px',
													border: 'none',
												}}
												// variant='pills'
												// activeKey={activeKey}
												// onSelect={(k) => setActiveKey(k)}
												// fill
											>
												<Tab eventKey="periodic" title="Periodic Reporting">
													<PeriodicReporting
														state="VALIDATED"
														isCoordinator={isCoordinator}
														isBeneficiary={isBeneficiary}
														userCompanies={companies}
														isProjectWp={isProjectWp}
														isEnabledToView={isOe || userAccess.isExternalAdmin}
													/>
												</Tab>

												<Tab eventKey="continuous" title="Continuous Reporting">
													<ContinuousReporting
														state="VALIDATED"
														isCoordinator={isCoordinator}
														isBeneficiary={isBeneficiary}
														isProjectWp={isProjectWp}
														isEnabledToView={isOe || userAccess.isExternalAdmin}
													/>
												</Tab>
											</Tabs>
										</Row>
									) : null}
								</>
							) : null}
						</WithPermission>
					</Route>
					{(isOe || project.streamConfigurationType === 'STREAM' || isCoordinator || isBeneficiary) && (
						<Route path={`/project/:projectId/:tab/expense-report`}>
							<WithPermission permission="view-draft-statements">
								{activeKey === 'expense-report' && (
									<>
										{project.streamConfigurationType === 'STREAM' ? (
											<MakeReporting
												optionsStatementTypes={optionsStatementTypes}
												draftStatements={draftStatements}
												draftStatementsMeta={draftStatementsMeta}
												statements={statements}
												localLang={localLang}
												handleSetAllStatements={handleSetAllStatements}
												isInCreate={isInCreate}
												setIsInCreate={setIsInCreate}
												isAdvancedSettings={project?.advancedConfig?.some((el) => el.name === 'advance_payment_percentage' && el.enabled)}
											/>
										) : null}
										{project.streamConfigurationType === 'WP' ? (
											<Row>
												<Tabs
													style={{
														marginTop: '50px',
														marginBottom: '20px',
														border: 'none',
													}}
													// variant='pills'
													// activeKey={activeKey}
													// onSelect={(k) => setActiveKey(k)}
													// fill
												>
													<Tab eventKey="periodic" title="Periodic Reporting">
														<PeriodicReporting
															state="DRAFT"
															isEnabledToView={isOe || userAccess.isExternalAdmin}
															isBeneficiary={isBeneficiary}
															userCompanies={companies}
															isProjectWp={isProjectWp}
															projectIndirectCostsPerc={project.indirectCostsPerc}
														/>
													</Tab>

													<Tab eventKey="continuous" title="Continuous Reporting">
														<ContinuousReporting
															state="DRAFT"
															isCoordinator={isCoordinator}
															isBeneficiary={isBeneficiary}
															isProjectWp={isProjectWp}
															isEnabledToView={isOe || userAccess.isExternalAdmin}
														/>
													</Tab>
												</Tabs>
											</Row>
										) : null}
									</>
								)}
							</WithPermission>
						</Route>
					)}
					<Route path={`/project/:projectId/:tab/dnsh`}>
						<WithPermission permission="view-rendicontazione-dnsh-tab">{activeKey === 'dnsh' && <Dnsh project={project} />}</WithPermission>
					</Route>
				</Switch>
			</div> */}
		</>
	);
};
export default Reporting;
